// Invite links on Internal → People: one person's links, and the click timeline of the one
// selected. Opened from the row menu ("Tracked links") or the "Link clicks" cell.
import { useEffect, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { CopyIcon, LinkIcon, PencilIcon } from 'lucide-react'
import { Button, Modal } from '@repo/ui'

import { useSnackbar } from 'contexts'
import type { Person } from 'hooks/api/usePeople'
import type { TrackedLink } from 'hooks/api/useTrackedLinks'
import { activityStamp } from 'utils'

import { SectionNote } from '../InternalAnalyticsPage/components/shared'
import { RenameTrackedLinkModal } from './RenameTrackedLinkModal'
import { TrackedLinkDetail } from './TrackedLinkDetail'

export const PersonTrackedLinksModal = ({
  person,
  links,
  open,
  onClose,
  onCreate,
}: {
  person: Person | null
  links: TrackedLink[]
  open: boolean
  onClose: () => void
  onCreate: () => void
}) => {
  const { open: notification } = useSnackbar()
  const [selected, setSelected] = useState<string | null>(null)
  const [renaming, setRenaming] = useState<TrackedLink | null>(null)

  useEffect(() => {
    if (open) setSelected(links[0]?.id ?? null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, person?.key])

  const name = person ? person.name || person.email || 'Unnamed' : ''

  return (
    <Modal
      title={`Tracked links · ${name}`}
      open={open}
      onClose={onClose}
      sx={{ width: 760 }}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button startIcon={<LinkIcon strokeWidth={1} />} onClick={onCreate}>
            Create tracked link
          </Button>
        </>
      }
    >
      <Stack spacing={4} mt={2}>
        {links.length === 0 ? (
          <SectionNote>No tracked links for {name} yet.</SectionNote>
        ) : (
          <Stack spacing={1} role="listbox" aria-label="Their links">
            {links.map((l) => {
              const visit = activityStamp(l.last_visit_at)
              return (
              <Stack
                key={l.id}
                role="option"
                aria-selected={selected === l.id}
                direction="row"
                alignItems="center"
                spacing={2}
                onClick={() => setSelected(l.id)}
                sx={{
                  p: 2,
                  borderRadius: 1,
                  border: 1,
                  borderColor: selected === l.id ? 'primary.main' : 'divider',
                  cursor: 'pointer',
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" noWrap>
                    {l.label}
                  </Typography>
                  <Typography variant="body3" color="text.secondary" noWrap>
                    /invite/{l.code} → {l.destination}
                    {l.campaign ? ` · ${l.campaign}` : ''}
                  </Typography>
                </Box>
                {/* VRG-705 — the shared last-activity stamp, inline: date + time and how long ago,
                    the full stamp and zone on hover. */}
                <Typography variant="body3" color="text.secondary" sx={{ whiteSpace: 'nowrap' }} title={visit?.title}>
                  {l.clicks} click{l.clicks === 1 ? '' : 's'}
                  {visit ? ` · ${visit.primary} (${visit.relative})` : ''}
                </Typography>
                {/* VRG-794: rename the label; the code and URL stay. */}
                <Button
                  variant="secondary"
                  aria-label={`Rename ${l.label}`}
                  title="Rename"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    setRenaming(l)
                  }}
                >
                  <PencilIcon strokeWidth={1} size={14} />
                </Button>
                <Button
                  variant="secondary"
                  aria-label={`Copy ${l.url}`}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    navigator.clipboard.writeText(l.url).then(
                      () => notification({ type: 'success', title: 'Link copied' }),
                      () => notification({ type: 'error', title: 'Could not copy' }),
                    )
                  }}
                >
                  <CopyIcon strokeWidth={1} size={14} />
                </Button>
              </Stack>
              )
            })}
          </Stack>
        )}
        {selected && <TrackedLinkDetail id={selected} />}
      </Stack>
      <RenameTrackedLinkModal link={renaming} onClose={() => setRenaming(null)} />
    </Modal>
  )
}
